/* ============================================================
   พนักงาน — คนที่รับผิดชอบงานในโปรเจ็กต์
   ไม่จำเป็นต้องมี user login (โฟร์แมน / ช่าง ส่วนใหญ่ไม่ได้ใช้ระบบ)
   ถ้ามี login ให้ผูกไว้ ระบบจะใส่เป็นทีมให้อัตโนมัติตอนคนนั้นสร้างโปรเจ็กต์
============================================================ */
import React, { useEffect, useState } from "react";
import { AutoComplete, Avatar, Col, DatePicker, Form, Input, Row, Select, Space, Tag, Tooltip } from "antd";
import { UserOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import CrudPage from "../../../components/ak/CrudPage";
import StatusTag from "../../../components/ak/StatusTag";
import ImageUpload from "../../../components/ak/ImageUpload";
import { EmployeeService } from "../../../service/Ak.service";
import OptionService from "../../../service/Options.service";
import { fileUrl } from "../../../utils/util";

const opt = OptionService();

export const DEPARTMENTS = ["Support", "จัดซื้อ", "สโตร์", "ก่อสร้าง", "บริหาร", "บัญชี", "อื่น ๆ"].map((v) => ({ value: v, label: v }));
const POSITIONS = ["โฟร์แมน", "ผู้ช่วยโฟร์แมน", "วิศวกรโยธา", "วิศวกรไฟฟ้า", "สถาปนิก", "ผู้จัดการโครงการ", "Support", "จัดซื้อ", "สโตร์", "หัวหน้าช่าง", "ช่างไฟฟ้า", "ช่างประปา"].map((v) => ({ value: v }));

export const EmployeeFormFields = ({ users = [], withLogin = true }) => (
  <Row gutter={12}>
    <Col xs={24} md={4}>
      <Form.Item name="prename" label="คำนำหน้า">
        <Select allowClear options={["นาย", "นาง", "นางสาว"].map((v) => ({ value: v, label: v }))} />
      </Form.Item>
    </Col>
    <Col xs={24} md={7}>
      <Form.Item name="firstname" label="ชื่อ" rules={[{ required: true, message: "กรอกชื่อ" }]}>
        <Input maxLength={100} />
      </Form.Item>
    </Col>
    <Col xs={24} md={7}>
      <Form.Item name="lastname" label="นามสกุล">
        <Input maxLength={100} />
      </Form.Item>
    </Col>
    <Col xs={24} md={6}>
      <Form.Item name="nickname" label="ชื่อเล่น">
        <Input maxLength={50} />
      </Form.Item>
    </Col>
    <Col xs={24} md={8}>
      <Form.Item name="department" label="แผนก">
        <Select allowClear options={DEPARTMENTS} />
      </Form.Item>
    </Col>
    <Col xs={24} md={8}>
      <Form.Item name="position" label="ตำแหน่งงาน">
        <AutoComplete allowClear options={POSITIONS} placeholder="เลือกหรือพิมพ์" filterOption={(i, o) => o.value.includes(i)} />
      </Form.Item>
    </Col>
    <Col xs={24} md={8}>
      <Form.Item name="tel" label="เบอร์โทร">
        <Input maxLength={50} />
      </Form.Item>
    </Col>
    <Col xs={24} md={8}>
      <Form.Item name="line_id" label="LINE ID">
        <Input maxLength={100} />
      </Form.Item>
    </Col>
    <Col xs={24} md={8}>
      <Form.Item name="email" label="อีเมล" rules={[{ type: "email", message: "รูปแบบอีเมลไม่ถูกต้อง" }]}>
        <Input maxLength={100} />
      </Form.Item>
    </Col>
    <Col xs={24} md={8}>
      <Form.Item name="start_date" label="วันเริ่มงาน">
        <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
      </Form.Item>
    </Col>
    {withLogin && (
      <Col span={24}>
        <Form.Item name="user_code" label="ผูกกับ User login (ถ้ามี)" extra="เว้นว่างได้ — พนักงานที่ไม่ได้ใช้ระบบไม่ต้องมี login">
          <Select allowClear showSearch optionFilterProp="label" options={users} placeholder="ไม่มี login" />
        </Form.Item>
      </Col>
    )}
    {withLogin && (
      <Col span={24}>
        <Form.Item name="photo_path" label="รูปพนักงาน">
          <ImageUpload type="employee" />
        </Form.Item>
      </Col>
    )}
    <Col span={24}>
      <Form.Item name="remark" label="หมายเหตุ">
        <Input.TextArea rows={2} maxLength={2000} />
      </Form.Item>
    </Col>
  </Row>
);

/* ฟอร์มส่ง DatePicker เป็น dayjs → API ต้องการ YYYY-MM-DD */
export const employeePayload = (v) => ({ ...v, start_date: v.start_date ? dayjs(v.start_date).format("YYYY-MM-DD") : null, user_code: v.user_code ?? null });

export default function EmployeePage() {
  const [users, setUsers] = useState([]);
  const [dep, setDep] = useState();

  useEffect(() => {
    opt.users().then((r) => setUsers(r.data?.data || []));
  }, []);

  return (
    <CrudPage
      title="พนักงาน"
      subtitle="ใช้กำหนดทีมผู้รับผิดชอบโปรเจ็กต์ — ไม่จำเป็นต้องมี user login"
      menu="master_employee"
      rowKey="emp_code"
      service={EmployeeService}
      addText="เพิ่มพนักงาน"
      modalWidth={820}
      extraParams={{ department: dep }}
      extraFilters={<Select allowClear placeholder="ทุกแผนก" value={dep} onChange={setDep} options={DEPARTMENTS} style={{ width: 160 }} />}
      toForm={(r) => ({ ...r, start_date: r.start_date ? dayjs(r.start_date) : null, user_code: r.user_code ? Number(r.user_code) : null })}
      toPayload={employeePayload}
      renderForm={(form, editing) => (
        <>
          <EmployeeFormFields users={users} />
          {editing && (
            <Form.Item name="active_status" label="สถานะ">
              <Select options={[{ value: "Y", label: "ทำงานอยู่" }, { value: "N", label: "ลาออก / ปิดใช้งาน" }]} style={{ maxWidth: 240 }} />
            </Form.Item>
          )}
        </>
      )}
      columns={() => [
        {
          title: "พนักงาน",
          key: "name",
          render: (_, r) => (
            <Space>
              <Avatar src={r.photo_path ? fileUrl(r.photo_path) : undefined} icon={<UserOutlined />} />
              <div>
                <div>
                  {[r.prename, r.firstname, r.lastname].filter(Boolean).join(" ")}
                  {r.nickname ? ` (${r.nickname})` : ""}
                </div>
                <div style={{ fontSize: 12, color: "#8c8c8c" }}>{r.emp_code}</div>
              </div>
            </Space>
          ),
        },
        { title: "แผนก", dataIndex: "department", render: (v) => v || "-" },
        { title: "ตำแหน่ง", dataIndex: "position", render: (v) => v || "-" },
        { title: "เบอร์โทร", dataIndex: "tel", render: (v) => v || "-" },
        {
          title: "Login",
          dataIndex: "username",
          render: (v) => (v ? <Tooltip title="มี user เข้าระบบ"><Tag color="blue">{v}</Tag></Tooltip> : <Tag>ไม่มี</Tag>),
        },
        { title: "โปรเจ็กต์ที่ดูแล", dataIndex: "active_projects", align: "center", render: (v) => Number(v || 0) },
        { title: "สถานะ", dataIndex: "active_status", render: (v) => <StatusTag value={v} /> },
      ]}
    />
  );
}
